import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import {
  Package,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@features/auth/contexts/AuthContext';
import * as pickupService from '@features/pickups/services/pickupService';
import { 
  getStatusLightBgColor,
  getStatusTextColor,
  formatStatusName
} from "@shared/utils/statusColors";

const PickupDashboardWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLivreur = user?.role === "livreur";

  // Fetch pickup statistics
  const { data: pickupStats, isLoading } = useQuery({
    queryKey: ["pickup-stats", user?.id],
    queryFn: async () => {
      if (isLivreur) {
        return pickupService.getLivreurPickups("");
      } else {
        return pickupService.getAllPickups();
      }
    },
    select: (data) => {
      const pickups = data?.pickups || [];
      return {
        total: pickups.length,
        pending: pickups.filter((p) => p.status === "pending").length,
        approved: pickups.filter((p) => p.status === "approved").length,
        in_transit: pickups.filter((p) => p.status === "in_transit").length,
        delivered: pickups.filter((p) => p.status === "signed").length,
        assigned: pickups.filter((p) => p.livreur).length,
        unassigned: pickups.filter((p) => !p.livreur).length,
      };
    },
  });

  const stats = pickupStats || {
    total: 0,
    pending: 0,
    approved: 0,
    in_transit: 0,
    delivered: 0,
    assigned: 0,
    unassigned: 0,
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t("Pickup Management")}
          </h2>
          <p className="text-muted-foreground">
            {isLivreur
              ? t("Manage your assigned pickups")
              : (isAdmin || isManager)
              ? t("Monitor and manage all pickup operations")
              : t("View pickup information")}
          </p>
        </div>
        <Button onClick={() => navigate("/pickups/management")}>
          {t("View All Pickups")}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Pickups */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("Total Pickups")}
                </p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status-based Cards */}
        {isLivreur ? (
          <>
            {/* In Transit for Livreur */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Truck className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("In Transit")}
                    </p>
                    <p className="text-2xl font-bold">{stats.in_transit}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delivered for Livreur */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("Delivered")}
                    </p>
                    <p className="text-2xl font-bold">{stats.delivered}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Approved for Livreur */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("Approved")}
                    </p>
                    <p className="text-2xl font-bold">{stats.approved}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Pending for Admin */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("Pending")}
                    </p>
                    <p className="text-2xl font-bold">{stats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Unassigned for Admin */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("Unassigned")}
                    </p>
                    <p className="text-2xl font-bold">{stats.unassigned}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assigned for Admin */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("Assigned")}
                    </p>
                    <p className="text-2xl font-bold">{stats.assigned}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t("Quick Actions")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => navigate("/pickups")}
            >
              <Package className="h-6 w-6" />
              <span>{t("View All Pickups")}</span>
            </Button>

            {(isAdmin || isManager) && (
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center gap-2"
                onClick={() => navigate("/pickups/create")}
              >
                <Package className="h-6 w-6" />
                <span>{t("Create Pickup")}</span>
              </Button>
            )}

            <Button
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-2"
              onClick={() => navigate("/pickups/management")}
            >
              <Truck className="h-6 w-6" />
              <span>{t("Management Dashboard")}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {(isAdmin || isManager) && (
        <Card>
          <CardHeader>
            <CardTitle>{t("Pickup Status Overview")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t("Pending Approval")}
                </span>
                <Badge
                  variant="outline"
                  className={`${getStatusLightBgColor('pending')} ${getStatusTextColor('pending')}`}
                >
                  {stats.pending}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t("Approved")}
                </span>
                <Badge 
                  variant="outline" 
                  className={`${getStatusLightBgColor('approved')} ${getStatusTextColor('approved')}`}
                >
                  {stats.approved}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t("In Transit")}
                </span>
                <Badge 
                  variant="outline" 
                  className={`${getStatusLightBgColor('in_transit')} ${getStatusTextColor('in_transit')}`}
                >
                  {stats.in_transit}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t("Completed")}
                </span>
                <Badge
                  variant="outline"
                  className={`${getStatusLightBgColor('delivered')} ${getStatusTextColor('delivered')}`}
                >
                  {stats.delivered}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PickupDashboardWidget;
